/**
 * CharacterDesignerNode — V2 (Sprint 9i migration)
 *
 * Migrated to use UnifiedBottomPanel with 4 tabs:
 * Config, Refine, Variants, Voice
 *
 * Uses DesignerNodeShell for shared UI (border, image, lightbox, error, handles, toolbar).
 * Unique features: editable name/description, voice section, Quick Info Bar.
 *
 * Width: 400px. Image aspect: 16:9 (aspect-video).
 */

import React, { memo, useState, useCallback, useMemo } from 'react'
import { Position, NodeProps, useReactFlow } from 'reactflow'
import { getThumbnailUrl, getAssetUrl } from '../../../../api/useAssetUrl'
import { executeNode } from '../../../../api/client'
import { useCanvasOptional } from '../../CanvasContext'
import {
  DesignerNodeShell,
  FloatingNodeLabel,
  UnifiedBottomPanel,
  CHARACTER_TABS,
  DEFAULT_TAB,
  ConfigTabContent,
  RefineTabContent,
  VariantsTabContent,
  VoiceTabContent,
  ShimmerPlaceholder,
  TypewriterText,
  useSequencedLoading,
  useDesignerVariantSelection,
  useBottomPanelAutoOpen,
  resolveDesignerImagePath,
  type HandleConfig,
  type SelectedReference,
  buildAssetCandidates,
  type HistoryEntry,
} from '../shared'
import { DEFAULT_STYLE } from '../../../../constants/styles'
import { useFeedbackMode } from '../../panels/useFeedbackMode'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import type { CharacterDesignerNodeData } from '../../../../generated/nodeDataTypes'

export type { CharacterDesignerNodeData }

const HANDLES: HandleConfig[] = [
  { id: 'in_upstream', type: 'target', position: Position.Top },
  { id: 'in_reference_images', type: 'target', position: Position.Left },
  { id: 'out_character_sheet', type: 'source', position: Position.Bottom },
  { id: 'out_image_path', type: 'source', position: Position.Bottom },
  { id: 'out_voice_profile', type: 'source', position: Position.Right },
]

const agentColor = AGENT_COLORS.character

const CHARACTER_EMPTY_ICON_PATH =
  'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'

const CharacterDesignerNode = memo(function CharacterDesignerNode(
  props: NodeProps<CharacterDesignerNodeData>
) {
  const { id, data, selected, dragging } = props
  const {
    cacheKey,
    in_name = '',
    in_role = '',
    in_style = DEFAULT_STYLE,
    in_description = '',
    in_reference_images,
    out_character_sheet,
    out_voice_profile,
    status = 'idle',
    progressMessage,
    error,
  } = data
  const projectId = useProjectId()

  const { setNodes, getNodes } = useReactFlow()
  const canvas = useCanvasOptional()

  const isRunning = status === 'running'

  // Image path resolution (shared utility)
  const imagePath = resolveDesignerImagePath(out_character_sheet)

  // Sequenced loading animation
  const {
    showSubtitleShimmer,
    subtitleActive,
    onSubtitleComplete,
  } = useSequencedLoading({ isRunning })

  // Bottom panel state
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      setNodes((nodes) => nodes.map((n) => ({ ...n, selected: n.id === id })))
    },
    [id, setNodes]
  )

  // History state
  const mainImageHistory = out_character_sheet?.images?.main
  const historyEntries = useMemo(
    () => (mainImageHistory?.history ?? []) as HistoryEntry[],
    [mainImageHistory?.history]
  )
  const pickedIndex = mainImageHistory?.picked_index ?? 0
  const batchPending = isRunning ? (data.batch_pending ?? 0) : 0
  const hasHistory = historyEntries.length > 1 || batchPending > 0

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  // Feedback mode
  const { feedbackText, setFeedbackText, handleRefine } = useFeedbackMode({
    nodeId: id,
    status,
  })

  // Shared variant selection (updates picked_index)
  const handleHistorySelect = useDesignerVariantSelection({
    nodeId: id,
    historyEntries,
    outputFieldName: 'out_character_sheet',
  })

  // Voice data
  const voiceName = out_voice_profile?.voice_name
  const voiceSampleUrl =
    out_voice_profile?.audio_sample && projectId
      ? getAssetUrl(projectId, out_voice_profile.audio_sample, 0)
      : null

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newDesc = e.target.value
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: newDesc } } : n
        )
      )
      canvas?.updateNodeData(id, { in_description: newDesc })
    },
    [id, setNodes, canvas]
  )

  // Config tab: generate handler
  const handleConfigGenerate = useCallback(
    (retryCount: number) => {
      if (isRunning || !projectId) return
      executeNode(projectId, id, undefined, retryCount)
    },
    [isRunning, projectId, id]
  )

  // Refine handler
  const onRefineSubmit = useCallback(() => {
    handleRefine()
  }, [handleRefine])

  // Style change handler
  const handleStyleChange = useCallback(
    (styleId: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_style: styleId } } : n
        )
      )
      canvas?.updateNodeData(id, { in_style: styleId })
    },
    [id, setNodes, canvas]
  )

  // Reference handling for ConfigTabContent
  const currentRefs = useMemo<SelectedReference[]>(() => {
    if (!in_reference_images?.length) return []
    const candidates = buildAssetCandidates(getNodes(), id)
    const pathToCandidate = new Map(candidates.map((c) => [c.assetPath, c]))
    return in_reference_images.map((path, i) => {
      const match = pathToCandidate.get(path)
      return {
        nodeId: match?.nodeId ?? `ref-${i}`,
        nodeType: match?.nodeType ?? 'image',
        label: match?.label ?? path.split('/').pop() ?? `Reference ${i + 1}`,
        assetPath: path,
        mediaType: (match?.mediaType ?? 'image') as 'image' | 'video',
      }
    })
  }, [in_reference_images, getNodes, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      const paths = refs.map((r) => r.assetPath)
      const newValue = paths.length > 0 ? paths : undefined
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_reference_images: newValue } } : n
        )
      )
      canvas?.updateNodeData(id, { in_reference_images: newValue })
    },
    [id, setNodes, canvas]
  )

  // Prompt change handler
  const handlePromptChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: value } } : n
        )
      )
      canvas?.updateNodeData(id, { in_description: value })
    },
    [id, setNodes, canvas]
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_name: newName } } : n
        )
      )
      canvas?.updateNodeData(id, { in_name: newName })
    },
    [id, setNodes, canvas],
  )

  // Cache bust for reference image thumbnails
  const cacheBust = cacheKey

  // Panel visibility: selected AND not running AND not dragging AND panel open
  // Exception: during regeneration, auto-open to Variants (handled by useBottomPanelAutoOpen)
  const panelVisible = !!selected && !dragging && bottomPanelOpen && (!isRunning || batchPending > 0)

  // Bottom panel (rendered via bottomPanel prop to avoid overflow-hidden clipping)
  const bottomPanel = (
    <UnifiedBottomPanel
      tabs={CHARACTER_TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      isVisible={panelVisible}
    >
      {(tab) => {
        if (tab === 'config' && projectId) {
          return (
            <ConfigTabContent
              nodeId={id}
              nodeType="character"
              prompt={in_description}
              onPromptChange={handlePromptChange}
              references={currentRefs}
              onReferencesChange={handleReferencesChange}
              currentStyleId={in_style}
              onStyleChange={handleStyleChange}
              isRunning={isRunning}
              onGenerate={handleConfigGenerate}
            />
          )
        }
        if (tab === 'refine') {
          return (
            <RefineTabContent
              feedbackText={feedbackText}
              onFeedbackTextChange={setFeedbackText}
              onSubmit={onRefineSubmit}
              isRunning={isRunning}
              nodeType="characterDesigner"
              selectedStyleId={in_style}
              onStyleChange={handleStyleChange}
            />
          )
        }
        if (tab === 'variants') {
          return (
            <VariantsTabContent
              history={historyEntries}
              pickedIndex={pickedIndex}
              onSelectVariant={handleHistorySelect}
              batchPending={batchPending}
              cacheKey={cacheBust}
              projectId={projectId}
              accentColor="purple"
              aspectRatio="16/9"
            />
          )
        }
        if (tab === 'voice') {
          return (
            <VoiceTabContent
              voiceName={voiceName}
              voiceSampleUrl={voiceSampleUrl}
              speechText={out_voice_profile?.speech_text}
            />
          )
        }
        return null
      }}
    </UnifiedBottomPanel>
  )

  return (
    <DesignerNodeShell
      nodeId={id}
      selected={selected}
      dragging={dragging}
      status={status}
      error={error}
      progressMessage={progressMessage}
      nodeName={in_name}
      agentTypeLabel="Character"
      agentColor={agentColor}
      width={400}
      imageAspect="aspect-video"
      handles={HANDLES}
      testId="character-designer-node"
      imagePath={imagePath}
      cacheKey={cacheKey}
      hasHistory={hasHistory}
      emptyIconPath={CHARACTER_EMPTY_ICON_PATH}
      onOpenVariants={() => openPanelToTab('variants')}
      bottomPanel={bottomPanel}
      floatingLabel={<FloatingNodeLabel name={in_name} fallback="Untitled Character" onRename={handleRename} />}
    >
      {/* Content Area */}
      <div className="pt-2 px-6 pb-6">
        {/* Role label */}
        <span className="text-xs font-medium" style={{ color: agentColor.active }}>
          {in_role || 'Character'}
        </span>

        {/* Description -- editable or animated */}
        <div className="mt-2 h-[60px]">
          {showSubtitleShimmer ? (
            <div className="space-y-1.5">
              <ShimmerPlaceholder width="100%" height={14} />
              <ShimmerPlaceholder width="80%" height={14} />
            </div>
          ) : isRunning && in_description ? (
            <TypewriterText
              text={in_description}
              isActive={subtitleActive}
              onComplete={onSubtitleComplete}
              className="text-sm text-muted-foreground leading-relaxed line-clamp-3"
              as="p"
            />
          ) : (
            <textarea
              data-testid="character-description-input"
              aria-label="Character description"
              value={in_description || ''}
              placeholder="Describe this character..."
              onChange={handleDescriptionChange}
              className="w-full h-full text-sm text-muted-foreground leading-relaxed bg-transparent border-none outline-none p-0 m-0 resize-none nodrag nowheel"
              rows={3}
            />
          )}
        </div>
      </div>

      {/* Quick Info Bar -- 3 columns: Config | Variants | Reference */}
      <div className="grid grid-cols-3 border-t border-border-subtle px-6">
        {/* Config Column */}
        <button
          data-testid="quick-info-config"
          onClick={(e) => {
            e.stopPropagation()
            openPanelToTab('config')
          }}
          className="py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle pr-3"
        >
          <span className="text-sm font-medium text-foreground">Config</span>
          <div className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        </button>

        {/* Variants Column */}
        <button
          data-testid="quick-info-variants"
          onClick={(e) => {
            e.stopPropagation()
            openPanelToTab('variants')
          }}
          className="px-3 py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle"
        >
          <span className="text-sm font-medium text-foreground">Variants</span>
          <div className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{
                backgroundColor:
                  historyEntries.length > 0 ? agentColor.active : 'var(--text-muted)',
              }}
            />
            <span className="text-sm font-medium text-foreground">
              {historyEntries.length}
            </span>
          </div>
        </button>

        {/* Reference Column */}
        <button
          data-testid="quick-info-reference"
          onClick={(e) => {
            e.stopPropagation()
            openPanelToTab('config')
          }}
          className="pl-3 py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag"
        >
          <span className="text-sm font-medium text-foreground">Reference</span>
          <div className="flex items-center gap-1">
            {(in_reference_images || []).slice(0, 2).map((path, i) => {
              const thumbUrl = projectId
                ? getThumbnailUrl(projectId, path, 32, cacheBust)
                : null
              return thumbUrl ? (
                <div key={i} className="w-6 h-6 rounded overflow-hidden border border-border">
                  <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                </div>
              ) : null
            })}
          </div>
        </button>
      </div>
    </DesignerNodeShell>
  )
})

CharacterDesignerNode.displayName = 'CharacterDesignerNode'
export default CharacterDesignerNode
